import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Users, Flame } from "lucide-react";
import { api } from "@/lib/api";

function formatNumber(n) {
  return new Intl.NumberFormat("fr-FR").format(n || 0);
}

export default function PublicStatsWidget() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get("/public/stats");
        if (mounted) setStats(r.data);
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  if (!stats) return null;

  const todayHolds = stats.today?.holds || 0;
  const totalHolds = stats.all_time?.holds || 0;
  const todayCounters = stats.today?.counters_started || 0;
  const totalCounters = stats.all_time?.counters_started || 0;

  const cards = [
    {
      Icon: Heart,
      value: todayHolds,
      label: "actes de courage",
      sub: "aujourd'hui",
      color: "#F97316",
      tone: "rgba(249, 115, 22, 0.15)",
      border: "rgba(249, 115, 22, 0.35)",
    },
    {
      Icon: Users,
      value: todayCounters,
      label: "compteurs lancés",
      sub: "aujourd'hui",
      color: "#10B981",
      tone: "rgba(16, 185, 129, 0.15)",
      border: "rgba(16, 185, 129, 0.35)",
    },
    {
      Icon: Flame,
      value: totalHolds + totalCounters,
      label: "victoires partagées",
      sub: "depuis l'ouverture",
      color: "#A78BFA",
      tone: "rgba(167, 139, 250, 0.15)",
      border: "rgba(167, 139, 250, 0.35)",
    },
  ];

  return (
    <section className="pb-16 lg:pb-20" data-testid="public-stats">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <span className="em-eyebrow">La communauté avance</span>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
          Tu n'es pas seul·e dans ce combat.
        </h2>
        <p className="mt-3 text-slate-300 max-w-2xl">
          Ces chiffres anonymes montrent les victoires silencieuses de ceux qui tiennent bon
          en ce moment. Chaque clic sur « Je tiens bon » compte ici.
        </p>

        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          {cards.map((c) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="rounded-2xl border p-6"
              style={{
                background: `linear-gradient(180deg, ${c.tone}, rgba(15, 23, 42, 0.9))`,
                borderColor: c.border,
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-xl grid place-items-center border"
                  style={{ background: `${c.color}25`, borderColor: `${c.color}55` }}
                >
                  <c.Icon className="w-5 h-5" style={{ color: c.color }} />
                </div>
                <div>
                  <div
                    className="font-[Outfit] font-bold text-3xl lg:text-4xl text-white tabular-nums"
                    data-testid={`stat-${c.label.split(" ")[0]}`}
                  >
                    {formatNumber(c.value)}
                  </div>
                  <div className="text-xs text-slate-400 uppercase tracking-widest">{c.sub}</div>
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-300">{c.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
